import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/catalyst/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useGetProviderDirectoryLocationQuery,
  useUpdateProviderDirectoryLocationMutation,
} from '@/services/api/providerDirectoryV2Service';

export const EditLocationModal = ({
  open,
  onClose,
  locationId,
}: {
  open: boolean;
  onClose: () => void;
  locationId: string;
}) => {
  const { data: location } = useGetProviderDirectoryLocationQuery(
    { locationId },
    { skip: !open || !locationId },
  );

  const [updateLocation, { isLoading }] =
    useUpdateProviderDirectoryLocationMutation();

  const [name, setName] = useState('');
  const [locationType, setLocationType] = useState('');
  const [status, setStatus] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open) return;
    if (!location) return;
    setName(location.name ?? '');
    setLocationType(location.location_type ?? '');
    setStatus(location.status ?? '');
    setNotes(location.notes ?? '');
  }, [location, open]);

  const canSave = useMemo(() => Boolean(name.trim()), [name]);

  const handleSave = async () => {
    if (!location) return;
    if (!name.trim()) {
      toast.error('Name is required.');
      return;
    }
    try {
      await updateLocation({
        locationId: location.id,
        patch: {
          name: name.trim(),
          location_type: locationType.trim() || null,
          status: status.trim() || null,
          notes: notes.trim() || null,
        },
      }).unwrap();
      toast.success('Location updated');
      onClose();
    } catch (err) {
      console.error('Failed to update location', err);
      toast.error('Failed to update location');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? onClose() : null)}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Edit location</DialogTitle>
          <DialogDescription>
            Update the label and metadata for this location/department.
          </DialogDescription>
        </DialogHeader>

        {!location ? (
          <div className="text-sm text-zinc-600">Loading…</div>
        ) : (
          <div className="space-y-3">
            <div>
              <div className="text-xs font-medium text-zinc-600">Name</div>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Location name…"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="text-xs font-medium text-zinc-600">Type</div>
                <Input
                  value={locationType}
                  onChange={(e) => setLocationType(e.target.value)}
                  placeholder="e.g. CLINICAL / BILLING / RECORDS…"
                />
              </div>
              <div>
                <div className="text-xs font-medium text-zinc-600">Status</div>
                <Input
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  placeholder="e.g. ACTIVE…"
                />
              </div>
            </div>

            <div>
              <div className="text-xs font-medium text-zinc-600">Notes</div>
              <textarea
                className="w-full rounded-md border border-zinc-200 bg-white p-2 text-sm min-h-[90px]"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes…"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose} disabled={isLoading}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={!canSave || isLoading}>
                Save
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
